import { Controller } from "@hotwired/stimulus"

/**
 * TOC Active Controller
 *
 * Tracks visible headings using Intersection Observer and highlights
 * the corresponding link in the Table of Contents sidebar.
 *
 * Usage:
 *   <nav data-controller="toc-active">
 *     <a href="#section-1" data-toc-active-target="link">Section 1</a>
 *     <a href="#section-2" data-toc-active-target="link">Section 2</a>
 *   </nav>
 */
export default class extends Controller {
  static targets = ["link"]

  connect() {
    this.setupObserver()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupObserver() {
    // Find all headings that have IDs
    const headings = document.querySelectorAll("h2[id], h3[id]")
    if (headings.length === 0) return

    const options = {
      root: null,
      // Account for sticky header offset: trigger when heading is 80px from top
      // and ignore bottom 80% of viewport to track "current" section accurately
      rootMargin: "-80px 0px -80% 0px",
      threshold: 0
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.setActiveLink(entry.target.id)
        }
      })
    }, options)

    headings.forEach(heading => this.observer.observe(heading))
  }

  setActiveLink(headingId) {
    // Remove active state from all links
    this.linkTargets.forEach(link => {
      link.classList.remove(
        "toc-link--active",
        "bg-white/50",
        "dark:bg-neutral-500/50",
        "text-fmt-blue",
        "dark:text-white"
      )
      link.removeAttribute("aria-current")
    })

    // Find and activate matching link
    const activeLink = this.linkTargets.find(
      link => link.getAttribute("href") === `#${headingId}`
    )

    if (activeLink) {
      activeLink.classList.add(
        "toc-link--active",
        "bg-white/50",
        "dark:bg-neutral-500/50",
        "text-fmt-blue",
        "dark:text-white"
      )
      // Set aria-current for accessibility (screen reader announcement)
      activeLink.setAttribute("aria-current", "true")
    }
  }
}
